import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errorMessage } from '../../lib/api.js';
import {
  useBlogs, useQuota, useTemplates, useWidgets, useInvalidate, useMutation, type Widget, type TemplateInfo,
} from '../../lib/queries.js';
import {
  Badge, Button, Card, EmptyState, ErrorBox, Input, Loading, Modal, PageHeader,
  Select, STATUS_TONE, useToast, cx,
  IconWidget, IconEye,
} from '../../components/ui/index.js';
import { STATUS_LABEL, num, pct, relative } from '../../lib/format.js';
import { TemplatePreviewModal } from './TemplatePreview.js';
import { WidgetLivePreviewModal } from './WidgetLivePreview.js';
import { hasRole, useRole } from '../../lib/auth.js';

function CreateWidgetModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const blogs = useBlogs();
  const templates = useTemplates('WIDGET');
  const invalidate = useInvalidate();
  const toast = useToast();
  const [form, setForm] = useState({ name: '', blogId: '', templateCode: 'card-grid', domain: '' });
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<TemplateInfo | null>(null);

  const blogId = form.blogId || blogs.data?.[0]?.id || '';

  const create = useMutation({
    mutationFn: () => api.post<{ data: Widget }>('/v1/widgets', {
      blogId,
      name: form.name,
      templateCode: form.templateCode,
      isDomainLocked: true,
      domains: form.domain ? [form.domain.trim()] : [],
    }),
    onSuccess: (r) => {
      invalidate(['widgets'], ['quota']);
      toast('สร้าง widget เรียบร้อย — ขั้นต่อไปคือคัดลอกโค้ดไปวางบนเว็บ');
      onClose();
      window.location.href = `/widgets/${r.data.id}`;
    },
    onError: (err) => setError(errorMessage(err)),
  });

  return (
    <>
    <Modal
      open={open}
      /*
       * กัน Escape ปิดฟอร์มสร้างทั้งใบตอนที่ผู้ใช้แค่อยากปิด modal ตัวอย่าง
       * — Modal ทั้งสองตัวผูก keydown ไว้ที่ document เหมือนกัน กด Escape ทีเดียว
       *   onClose จะยิงพร้อมกันทั้งคู่ ถ้าไม่กันไว้ตรงนี้
       */
      onClose={() => { if (!preview) onClose(); }}
      title="สร้าง widget ใหม่" size="lg"
      footer={<>
        <Button variant="ghost" onClick={onClose}>ยกเลิก</Button>
        <Button loading={create.isPending} disabled={!form.name || !blogId}
                onClick={() => { setError(null); create.mutate(); }}>สร้าง widget</Button>
      </>}
    >
      <div className="space-y-4">
        {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

        <Input label="ชื่อ widget" required autoFocus placeholder="เช่น หน้าแรกเว็บบริษัท"
               value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
               hint="ชื่อนี้ใช้ในหลังบ้านเท่านั้น ผู้ชมไม่เห็น" />

        <Select label="ดึงบทความจากบล็อก" required value={blogId}
                onChange={(e) => setForm({ ...form, blogId: e.target.value })}>
          {blogs.data?.map((b) => <option key={b.id} value={b.id}>{b.title}</option>)}
        </Select>

        <div>
          <span className="label">รูปแบบการแสดงผล</span>
          <div className="grid gap-2 sm:grid-cols-2">
            {templates.data?.map((t) => (
              /*
               * ปุ่มดูตัวอย่างต้องอยู่นอกปุ่มเลือก — <button> ซ้อน <button> เป็น HTML
               * ที่ไม่ถูกต้อง เบราว์เซอร์จะแยกโครงสร้างเองแล้วคลิกเพี้ยน จึงใช้ div
               * ครอบแล้ววางปุ่มดูตัวอย่างทับมุมขวาบนแทน
               */
              <div key={t.code} className="relative">
                <button
                  type="button" disabled={!t.available}
                  onClick={() => setForm({ ...form, templateCode: t.code })}
                  className={cx(
                    'w-full rounded-lg border p-3 text-left transition-colors',
                    form.templateCode === t.code ? 'border-[var(--brand)] bg-[var(--brand-soft)]'
                                                 : 'border-[var(--line)]',
                    t.available ? 'hover:border-[var(--line)]' : 'cursor-not-allowed opacity-50',
                  )}
                >
                  {/* เว้นที่ขวาให้ปุ่มดูตัวอย่างไม่ทับชื่อ template */}
                  <div className="flex items-center justify-between gap-2 pe-8">
                    <span className="text-sm font-semibold text-[var(--ink)]">{t.name}</span>
                    {/*
                      * ป้ายต้องบอกเหตุผลที่ถูก: comingSoon = ยังไม่เปิดให้ใช้ (อัปเกรดไปก็ยังใช้ไม่ได้)
                      * ส่วนชื่อแพ็กเกจใช้เฉพาะตอนที่แพ็กเกจไม่ถึงจริง ๆ เท่านั้น
                      */}
                    {t.comingSoon
                      ? <Badge tone="gray">เร็ว ๆ นี้</Badge>
                      : !t.available && <Badge tone="amber">ต้องใช้ {t.minPlan}</Badge>}
                  </div>
                  <p className="mt-0.5 text-xs text-[var(--ink-3)]">{t.description}</p>
                </button>

                <button
                  type="button"
                  onClick={() => setPreview(t)}
                  title={`ดูตัวอย่าง ${t.name}`}
                  aria-label={`ดูตัวอย่าง ${t.name}`}
                  className="absolute end-2 top-2 flex h-7 w-7 items-center justify-center rounded-md
                             text-[var(--ink-3)] transition-colors hover:bg-[var(--sunken)]
                             hover:text-[var(--ink)] focus:outline-none focus-visible:ring-2
                             focus-visible:ring-[var(--brand)]"
                >
                  <IconEye size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <Input
          label="โดเมนเว็บไซต์ที่จะฝัง widget" placeholder="www.company.co.th"
          value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })}
          hint="widget จะแสดงเฉพาะบนโดเมนที่อนุญาตเท่านั้น เพื่อกันคนอื่นเอาคีย์ไปใช้ — เพิ่มโดเมนอื่นภายหลังได้"
        />
      </div>
    </Modal>

    {/*
      * วางนอก <Modal> โดยตั้งใจ ถ้าซ้อนเป็นลูกจะโดน animate-fade-in ของกล่องแม่
      * สร้าง containing block ให้ position:fixed แล้ว modal ตัวอย่างจะถูกบีบอยู่
      * ในกรอบแม่และโดน overflow-y-auto ตัดหัวท้าย
      */}
    <TemplatePreviewModal
      template={preview}
      onClose={() => setPreview(null)}
      onSelect={(code) => setForm({ ...form, templateCode: code })}
    />
    </>
  );
}

export function WidgetListPage() {
  const widgets = useWidgets();
  const quota = useQuota();
  const role = useRole();
  const [creating, setCreating] = useState(false);
  const [previewing, setPreviewing] = useState<Widget | null>(null);

  const canCreate = hasRole(role, 'ADMIN');
  const atLimit = quota.data ? quota.data.widgets.used >= quota.data.widgets.max : false;

  if (widgets.isLoading) return <Loading />;
  if (widgets.error) return <ErrorBox error={widgets.error} onRetry={() => void widgets.refetch()} />;

  return (
    <>
      <PageHeader
        title="Widget"
        description={quota.data
          ? `ใช้ไป ${quota.data.widgets.used} จาก ${quota.data.widgets.max} widget ในแพ็กเกจปัจจุบัน`
          : 'กล่องแสดงบทความที่เอาไปฝังบนเว็บไซต์ของคุณ'}
        actions={canCreate && (
          <Button onClick={() => setCreating(true)} disabled={atLimit}
                  title={atLimit ? 'ถึงเพดานของแพ็กเกจแล้ว' : undefined}>+ สร้าง widget</Button>
        )}
      />

      {!widgets.data?.length ? (
        <EmptyState
          icon={<IconWidget size={22} />} title="ยังไม่มี widget"
          description="widget คือกล่องแสดงบทความที่คุณคัดลอกโค้ด 1 บรรทัดไปวางบนเว็บบริษัทได้เลย — ไม่ต้องแก้ระบบเดิม"
          action={canCreate && <Button onClick={() => setCreating(true)}>สร้าง widget แรก</Button>}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {widgets.data.map((w) => {
            const ctr = w.impressionCount > 0 ? (w.clickCount / w.impressionCount) * 100 : 0;
            const stale = w.lastSeenAt
              ? Date.now() - new Date(w.lastSeenAt).getTime() > 7 * 86400_000
              : true;
            return (
              <Card key={w.id} className="flex flex-col p-5 transition-shadow hover:shadow-md">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link to={`/widgets/${w.id}`}
                          className="block truncate text-base font-semibold text-[var(--ink)] hover:text-[var(--brand)]">
                      {w.name}
                    </Link>
                    <p className="mt-0.5 text-xs text-[var(--ink-3)]">{w.templateCode}</p>
                  </div>
                  <Badge tone={STATUS_TONE[w.status] ?? 'gray'}>{STATUS_LABEL[w.status] ?? w.status}</Badge>
                </div>

                <dl className="mt-4 grid grid-cols-3 gap-2 text-center">
                  {[
                    ['Impression', num(w.impressionCount)],
                    ['คลิก', num(w.clickCount)],
                    ['CTR', pct(ctr)],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-lg bg-[var(--sunken)] py-2">
                      <dd className="text-base font-bold tabular-nums text-[var(--ink)]">{value}</dd>
                      <dt className="text-[11px] text-[var(--ink-3)]">{label}</dt>
                    </div>
                  ))}
                </dl>

                <div className="mt-3 flex items-center gap-2 text-xs">
                  {stale ? (
                    <Badge tone="amber">
                      {w.lastSeenAt ? `ไม่มีสัญญาณตั้งแต่ ${relative(w.lastSeenAt)}` : 'ยังไม่เคยถูกเรียกใช้'}
                    </Badge>
                  ) : (
                    <Badge tone="green">ทำงานอยู่ · เห็นล่าสุด{relative(w.lastSeenAt)}</Badge>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-between gap-2 border-t border-[var(--line-soft)] pt-3">
                  <span className="truncate font-mono text-[11px] text-[var(--ink-3)]">{w.publicKey}</span>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button size="sm" variant="ghost" icon={<IconEye size={14} />}
                            onClick={() => setPreviewing(w)}>ดูตัวอย่าง</Button>
                    <Link to={`/widgets/${w.id}`}><Button size="sm" variant="secondary">ตั้งค่า</Button></Link>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <CreateWidgetModal open={creating} onClose={() => setCreating(false)} />
      <WidgetLivePreviewModal widget={previewing} onClose={() => setPreviewing(null)} />
    </>
  );
}
